/**
 * The dynamic values a name can carry, and how a name string splits into text
 * and tokens. A name is stored as plain text with tokens in braces
 * ("Sprint {date}"); lib/dynamicValues resolves them at apply, and
 * components/DynamicTextField shows them as pills while editing.
 *
 * Two kinds: the static registry (DYNAMIC_TOKENS: the apply date), and the
 * parent item's values — its name and one token per column (parentTokens),
 * built from whatever parent board the editor knows. A parent token is
 * recognised by its shape wherever it appears, so a stored name shows its
 * pills even where no parent board is loaded.
 *
 * Adding a static value: one entry here (token, label, group, preview) and
 * its resolution in resolveName (lib/dynamicValues, mirrored in
 * spt/src/helpers/dynamic-values.ts).
 */
import { canonicalType } from '../Config';
import { applyBaseDate, DATE_TOKEN, formatDate, NAME_TOKEN_TYPES, PARENT_NAME_TOKEN, PARENT_TOKEN_SRC, parentText, parentToken, parseParentToken } from './dynamicValues';

/**
 * token        the text stored in the name
 * label        what the pill and the menu show
 * group        the menu section
 * description  the menu's hint for it
 * preview(ctx) today's value, for the menu and the preview line
 */
export const DYNAMIC_TOKENS = Object.freeze([
  {
    token: DATE_TOKEN,
    label: 'Apply date',
    group: 'Date',
    description: 'The day the template is applied, in your timezone',
    preview: (ctx) => formatDate(ctx.date),
  },
]);

export const PARENT_GROUP = 'Parent item';

/** A parent token's entry, built from the token itself: its label is the column's title. */
function parentDef(token) {
  const t = parseParentToken(token);
  if (!t) return null;
  const name = t.kind === 'name';
  return {
    token,
    label: name ? 'Item name' : t.column.title,
    group: PARENT_GROUP,
    description: name ? "The parent item's name, when the template is applied" : `The parent item's ${t.column.title}, when the template is applied`,
    preview: (ctx) => {
      if (!ctx || !ctx.parent) return '';
      return name ? ctx.parent.name || '' : parentText(ctx.parent, t.column);
    },
  };
}

/** The parent item's values for a column list: Item name, then one per column a name can carry, in board order. */
export function parentTokens(parentColumns) {
  if (!parentColumns) return [];
  const out = [parentDef(PARENT_NAME_TOKEN)];
  for (const c of parentColumns) {
    const type = canonicalType(c.type);
    if (NAME_TOKEN_TYPES.includes(type)) out.push(parentDef(parentToken({ type, title: c.title })));
  }
  return out;
}

export function tokenDef(token, tokens = DYNAMIC_TOKENS) {
  return tokens.find((t) => t.token === token) || parentDef(token);
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * "Sprint {date}" → [{ kind: 'text', text: 'Sprint ' }, { kind: 'token', token: '{date}', def }].
 * Only known tokens become token segments; any other braces stay text.
 */
export function tokenSegments(value, tokens = DYNAMIC_TOKENS) {
  const s = String(value == null ? '' : value);
  if (!s) return [];
  const re = new RegExp([...tokens.map((t) => escapeRe(t.token)), PARENT_TOKEN_SRC].join('|'), 'g');
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ kind: 'text', text: s.slice(last, m.index) });
    out.push({ kind: 'token', token: m[0], def: tokenDef(m[0], tokens) });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ kind: 'text', text: s.slice(last) });
  return out;
}

/**
 * The context previews resolve against: today in the viewer's timezone, and
 * the parent item when the caller has one (null = known to have none, so
 * its tokens preview as nothing; left out = unknown, so they stay as typed).
 */
export function previewContext(timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone, now = new Date(), parent) {
  const ctx = { date: applyBaseDate(timeZone, now) };
  if (parent !== undefined) ctx.parent = parent;
  return ctx;
}

/** The menu's sections for a search query: [{ group, items: [def] }], the registry's groups then the parent item's. */
export function groupedTokens(query = '', tokens = DYNAMIC_TOKENS, parentColumns) {
  const q = query.trim().toLowerCase();
  const hits = [...tokens, ...parentTokens(parentColumns)].filter(
    (t) => !q || t.label.toLowerCase().includes(q) || t.group.toLowerCase().includes(q) || t.token.toLowerCase().includes(q),
  );
  const groups = [];
  for (const t of hits) {
    let g = groups.find((x) => x.group === t.group);
    if (!g) groups.push((g = { group: t.group, items: [] }));
    g.items.push(t);
  }
  return groups;
}
